import axios from 'axios';
import { getDeviceId } from '../../lib/deviceId';

const AUTH_SERVICE_URL = process.env.REACT_APP_AUTH_SERVICE_URL || 'http://localhost:4000';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

let accessToken = localStorage.getItem('accessToken');
let refreshToken = localStorage.getItem('refreshToken');

// Single-flight refresh, shared by BOTH clients below.
//
// Refresh tokens rotate and the old one is revoked the moment it is spent, so
// several requests 401ing together must not each fire their own refresh: the
// first would succeed and the rest would present a token that no longer
// exists, land in the catch block, and wipe the session the first had just
// restored. Every org-admin page fires requests in parallel, so that raced
// once every 15 minutes — which read as "it logs me out constantly".
// Module-level rather than per-client because axiosClient and apiClient share
// one session and can 401 in the same tick.
let refreshing = null;

const runRefresh = () => {
  if (!refreshing) {
    // withCredentials: the httpOnly session cookie is the third factor —
    // refresh fails without it even with valid tokens.
    refreshing = axios
      .post(`${AUTH_SERVICE_URL}/auth/refresh-token`, { refreshToken }, { withCredentials: true })
      .then((response) => {
        accessToken = response.data.accessToken;
        refreshToken = response.data.refreshToken;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        return accessToken;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
};

const attachInterceptors = (client) => {
  client.interceptors.request.use((config) => {
    if (accessToken && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    // Keys the account-recovery rate limit per browser rather than per IP, so
    // one person forgetting their password doesn't spend a whole office's
    // budget. Not a credential — see lib/deviceId.js.
    const deviceId = getDeviceId();
    if (deviceId) config.headers['X-Device-Id'] = deviceId;
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        // Nothing to refresh with — a failed login is a 401 too, and turning
        // that into a refresh attempt reloads the page out from under the
        // form instead of showing "invalid credentials".
        if (!refreshToken) return Promise.reject(error);

        try {
          const fresh = await runRefresh();

          originalRequest.headers.Authorization = `Bearer ${fresh}`;
          return client(originalRequest);
        } catch (refreshError) {
          // In-memory too, or the requests still in flight keep presenting a
          // token this session no longer owns until the reload lands.
          accessToken = null;
          refreshToken = null;
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      // ─── The firm has no active subscription ────────────────────────────
      //
      // 402 is raised by BOTH services for exactly one condition, which is why
      // that code was chosen: every other 4xx already means a dozen things, and
      // the client has to tell "your firm is not approved yet" apart from "you
      // may not do that" to know whether redirecting is the right answer.
      //
      // Handled here rather than page by page because the state changes
      // mid-session: a super admin suspending a firm should land its staff on
      // the plans screen wherever they happen to be, not leave them looking at
      // a page of silently failed requests.
      if (error.response?.status === 402 && error.response?.data?.code === 'SUBSCRIPTION_INACTIVE') {
        // Guarded, or the plans screen's own calls bounce it to itself for ever.
        // Guarded, or that screen's own calls bounce it to itself for ever.
        if (!window.location.pathname.startsWith('/subscription')) {
          window.location.href = '/subscription';
        }
      }

      return Promise.reject(error);
    }
  );

  return client;
};

// Auth-service client: login, users, organizations, roles/permissions
const axiosClient = attachInterceptors(
  axios.create({ baseURL: AUTH_SERVICE_URL, timeout: 10000 })
);

// Tasks-service client: clients, compliance, tasks (hrms-tasks-app, port 3001)
const apiClient = attachInterceptors(
  axios.create({ baseURL: API_URL, timeout: 10000 })
);

export const setTokens = (access, refresh) => {
  accessToken = access;
  refreshToken = refresh;
};

export const clearTokens = () => {
  accessToken = null;
  refreshToken = null;
};

export { apiClient };
export default axiosClient;
